import { useEffect, useRef, useState } from 'react';

import { Toast } from 'primereact/toast';

import {
  useGetDrugsQuery,
  useGetKickOutCommentsQuery,
  useHeatNotUsedMutation,
  useKickOutRecipMutation,
} from '@/app/services/inClinic';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import type { ImplantSession } from '../../hooks/useImplantSession';
import { getFriendlyError } from '../../utils/errorMessages';
import { isValidDecimalInput } from '../decimalInput';
import { KickOutCommentPicker } from '../KickOutCommentPicker';
import type { KickOutTarget } from '../RecipPanel/kickOutLogic';
import { shouldUseKickOutEndpoint } from '../RecipPanel/kickOutLogic';

interface HeatNotUsedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: KickOutTarget;
  session: ImplantSession;
  canWrite: boolean;
  onSuccess: () => void;
}

const CYSTORELIN_DEFAULTS = { dose: '2', unit: 'mL', name: 'Cystorelin' };

export function HeatNotUsedDialog({
  open,
  onOpenChange,
  target,
  session,
  canWrite,
  onSuccess,
}: HeatNotUsedDialogProps) {
  const isKickOut = shouldUseKickOutEndpoint(target.recipStatus);
  const useReadableErrors = useFeatureFlag('Replace_tech_error_with_readable');

  const [description, setDescription] = useState('');
  const [implantCidr, setImplantCidr] = useState(false);
  const [drugsUsed, setDrugsUsed] = useState(false);
  const [drugId, setDrugId] = useState('');
  const [dose, setDose] = useState('');
  const [unit, setUnit] = useState('');
  const errorToast = useRef<Toast>(null);

  const { data: drugs = [] } = useGetDrugsQuery();
  const { data: kickOutComments = [] } = useGetKickOutCommentsQuery();
  const [heatNotUsed, { isLoading: hnuLoading }] = useHeatNotUsedMutation();
  const [kickOutRecip, { isLoading: koLoading }] = useKickOutRecipMutation();
  const isLoading = hnuLoading || koLoading;

  useEffect(() => {
    if (!open) {
      setDescription('');
      setImplantCidr(false);
      setDrugsUsed(false);
      setDrugId('');
      setDose('');
      setUnit('');
    }
  }, [open]);

  useEffect(() => {
    if (drugsUsed && drugs.length > 0 && !drugId) {
      const cystorelin = drugs.find((d) =>
        d.name?.toLowerCase().includes('cystorelin'),
      );
      if (cystorelin) {
        setDrugId(cystorelin.id);
        setDose(CYSTORELIN_DEFAULTS.dose);
        setUnit(CYSTORELIN_DEFAULTS.unit);
      }
    }
  }, [drugsUsed, drugs, drugId]);

  const toggleComment = (comment: string) => {
    setDescription((prev) => {
      if (!prev) return comment;
      if (prev === comment) return '';
      const parts = prev
        .split(' - ')
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.includes(comment)) {
        return parts.filter((p) => p !== comment).join(' - ');
      }
      return [...parts, comment].join(' - ');
    });
  };

  const handleSubmit = async () => {
    if (!session.vetId || !session.techId) {
      errorToast.current?.show({
        severity: 'error',
        summary: 'Missing required fields',
        detail:
          'Please select a Vet and Tech in the toolbar before submitting.',
        life: 4000,
      });
      return;
    }

    if (isKickOut) {
      const result = await kickOutRecip({
        recipId: target.recipId,
        yardId: session.yardId,
        vetId: session.vetId,
        techId: session.techId,
      });
      if ('error' in result) {
        errorToast.current?.show({
          severity: 'error',
          summary: 'Kick Out failed',
          detail: getFriendlyError(
            result.error as { status: number | string; data?: string },
            'Kick Out',
            useReadableErrors,
          ),
          life: 4000,
        });
        return;
      }
    } else {
      const result = await heatNotUsed({
        recipId: target.recipId,
        recipQueueId: target.recipQueueId,
        yardId: session.yardId,
        vetId: session.vetId,
        techId: session.techId,
        description,
        implantCidr,
        drugId: drugsUsed ? drugId : '',
        dose: drugsUsed ? dose : '',
        unit: drugsUsed ? unit : '',
        chuteNumber: session.chuteNumber,
      });
      if ('error' in result) {
        errorToast.current?.show({
          severity: 'error',
          summary: 'Heat Not Used failed',
          detail: getFriendlyError(
            result.error as { status: number | string; data?: string },
            'Heat Not Used',
            useReadableErrors,
          ),
          life: 4000,
        });
        return;
      }
    }
    onSuccess();
  };

  const confirmDisabled =
    isLoading || !canWrite || (!isKickOut && !description);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="md:max-w-lg"
        onKeyDown={(e) => {
          if (
            e.key === 'Enter' &&
            !confirmDisabled &&
            (e.target as HTMLElement).tagName !== 'INPUT' &&
            (e.target as HTMLElement).tagName !== 'TEXTAREA'
          ) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <Toast ref={errorToast} />
        <DialogHeader>
          <DialogTitle>{isKickOut ? 'Kick Out' : 'Heat Not Used'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          {!isKickOut && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">Reason</span>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter reason..."
                />
              </div>

              <KickOutCommentPicker
                comments={kickOutComments}
                description={description}
                onSelect={toggleComment}
              />

              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={implantCidr}
                  onCheckedChange={(checked) => setImplantCidr(!!checked)}
                />
                Implant CIDR
              </label>

              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={drugsUsed}
                  onCheckedChange={(checked) => {
                    setDrugsUsed(!!checked);
                    if (!checked) {
                      setDrugId('');
                      setDose('');
                      setUnit('');
                    }
                  }}
                />
                Drugs Used
              </label>

              {drugsUsed && (
                <div className="rounded-lg border p-3">
                  <div className="grid grid-cols-3 gap-2">
                    <Select
                      value={drugId}
                      onChange={(val) => setDrugId(String(val))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Drug">
                          {(value) => {
                            const drug = drugs.find((d) => d.id === value);
                            return drug?.name ?? 'Drug';
                          }}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {drugs.map((drug) => (
                          <SelectItem key={drug.id} value={drug.id}>
                            {drug.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Dose"
                      value={dose}
                      onChange={(e) => {
                        if (isValidDecimalInput(e.target.value))
                          setDose(e.target.value);
                      }}
                      inputMode="decimal"
                    />
                    <Input
                      placeholder="Unit"
                      value={unit}
                      onChange={(e) => setUnit(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {isKickOut && (
            <p className="text-muted-foreground text-sm">
              This recipient will be kicked out and marked Open.
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={confirmDisabled}
          >
            {isLoading ? 'Processing...' : isKickOut ? 'Kick Out' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
